import { EventSeverity, EventTrigger } from '@cobliteam/common-types';
import { useTranslation } from '@cobliteam/i18n';
import Tag, { type TagProps } from '@cobliteam/ui/components/Tag';
import Icon from '@cobliteam/ui/icons';
import { cn } from '@cobliteam/ui/utils';
import TagFeatures from '@cobliteam/ui-business/components/Tag/Features';

export const getMediaEventBadge = (eventTrigger: EventTrigger) => {
  switch (eventTrigger) {
    case EventTrigger.COLLISION:
      return <TagFeatures.Beta variant='outlined' label='Beta' />;
    default:
      return;
  }
};

type TagEventSeverityProps = {
  severity?: EventSeverity;
  iconEnd?: React.ReactNode;
  lineThrough?: boolean;
} & Partial<TagProps>;

const TagEventSeverity = ({ severity, iconEnd, lineThrough, className, ...props }: TagEventSeverityProps) => {
  const { t } = useTranslation('media');

  const classNameTag = cn(className, lineThrough && 'line-through');

  const propsTagMap = {
    [EventSeverity.LOW]: { label: t('severity.low'), color: 'default' },
    [EventSeverity.MEDIUM]: {
      label: t('severity.medium'),
      color: 'attention',
      icon: <Icon name='octagon-alert' />,
    },
    [EventSeverity.HIGH]: {
      label: t('severity.high'),
      color: 'warning',
      icon: <Icon name='triangle-alert' />,
    },
    [EventSeverity.HIGHER]: {
      label: t('severity.higher'),
      color: 'danger-dark',
      icon: <Icon name='siren' />,
    },
  } satisfies Record<EventSeverity, TagProps>;

  if (!severity) {
    return (
      <div className='flex items-center gap-1'>
        <Icon name='loader' size='sm' />
        <span>{t('severity.processing')}</span>
      </div>
    );
  }

  const propsTag = propsTagMap[severity];

  return <Tag variant='surface' size='sm' iconEnd={iconEnd} className={classNameTag} {...propsTag} {...props} />;
};

export default TagEventSeverity;
